import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MobileNavigation from '../../components/MobileNavigation';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import { useNavigate, useLocation } from 'react-router-dom';
import BannerImage from '../../assets/nearby-merchants/banner-1.png';
import SpinnerIcon from '../../assets/spinner3.svg';
import { useUser } from '../../contexts/user';
import { useSearch } from '../../contexts/search';
import useCartActions from '../../hooks/useCartActions';
import Toast from '../../components/Toast';
import BrowseCategories from '../../components/Home/BrowseCategories';
import { ratingToPercent } from '../../utils/rating';
import FallbackBanner from '../../assets/banner-1.svg';
import FallbackCategory from '../../assets/nearby-merchants/1.png';
import SingleProductDetails from '../../components/MerchantDetails/SingleProductDetails';
import log from '../../utils/logger';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import DOMPurify from 'dompurify';
import api from '../../api/api';
import { useAppliedFilters } from '../../hooks/useAppliedFilters';
import { useFilterConfig } from '../../contexts/filters';
import { DRIVE_SPEED_KMH, CAROUSEL } from '../../config/appConfig';
import { applyProductFilters, collectProductCategories } from '../../utils/filters';

const GlobalSearch = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm } = useSearch();
  const location = useLocation();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({
    merchants: [],
    productsByMerchant: {},
  });
  const [viewBy, setViewBy] = useState('products');
  const [loading, setLoading] = useState(false);
  useScrollRestoration(!loading);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const { user, idToken, adminImpersonate } = useUser();
  const { addToCart, getMaxVariantFromCart, getVariantQuantitiesFromCart, changeQuantity, toastMessage, toastType } = useCartActions();
  const appliedFilters = useAppliedFilters();
  const { setFilterConfig, resetFilterConfig } = useFilterConfig();
  const [variantDecision, setVariantDecision] = useState(null);

  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
  }, [location.state, setSearchTerm]);

  useEffect(() => {
    return () => setSearchTerm('');
  }, [setSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (!debouncedSearchTerm.trim()) {
      setSearchResults({ merchants: [], productsByMerchant: {} });
      return;
    }

    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await api.get('/buyer/search/global', {
          params: {
            search: debouncedSearchTerm,
            language: i18n.language,
            adminImpersonateToken: adminImpersonate || null,
          },
          headers:
            user && idToken ? { Authorization: `Bearer ${idToken}` } : {},
        });
        const { merchants, productsByMerchant } = response.data.response.data;

        const filteredProductsByMerchant = Object.fromEntries(
          Object.entries(productsByMerchant).filter(
            ([, { products }]) => products && products.length > 0,
          ),
        );

        setSearchResults({
          merchants,
          productsByMerchant: filteredProductsByMerchant,
        });
      } catch (err) {
        log.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [debouncedSearchTerm, i18n.language]);

  const scrollRow = (merchantConfigId, direction) => {
    const container = document.getElementById(
      `products-row-${merchantConfigId}`,
    );
    if (container) {
      container.scrollBy({
        left: direction === 'left' ? -CAROUSEL.featuredScrollPx : CAROUSEL.featuredScrollPx,
        behavior: 'smooth',
      });
    }
  };

  const allProducts = useMemo(() => {
    return Object.values(searchResults.productsByMerchant || {}).flatMap(
      (entry) => (Array.isArray(entry?.products) ? entry.products : []),
    );
  }, [searchResults.productsByMerchant]);

  const availableCategories = useMemo(
    () => collectProductCategories(allProducts, i18n.language),
    [allProducts, i18n.language],
  );

  useEffect(() => {
    if (allProducts.length === 0) {
      resetFilterConfig();
      return undefined;
    }
    setFilterConfig({ mode: 'products', categories: availableCategories });
    return () => resetFilterConfig();
  }, [allProducts.length, availableCategories, setFilterConfig, resetFilterConfig]);

  const filteredProductsByMerchant = useMemo(() => {
    const out = {};
    Object.entries(searchResults.productsByMerchant || {}).forEach(
      ([id, entry]) => {
        const filtered = applyProductFilters(
          entry?.products || [],
          appliedFilters,
          i18n.language,
        );
        if (filtered.length > 0) {
          out[id] = { ...entry, products: filtered };
        }
      },
    );
    return out;
  }, [searchResults.productsByMerchant, appliedFilters, i18n.language]);

  const openVariantDecisionModal = (data) => {
    setVariantDecision(data);
  };

  const handleVariantQuantityIntent = (product, type) => {
    const { item } = getMaxVariantFromCart(product._id);

    // no variants → normal behavior
    if (!product.variants?.length) {
      changeQuantity(product, type);
      return;
    }

    openVariantDecisionModal({
      product,
      variantItem: item,
      type,
    });
  };

  const handleModifySameVariant = () => {
    if (!variantDecision) {
      return;
    }

    const { product, variantItem, type } = variantDecision;

    changeQuantity(
      {
        ...product,
        variantId: variantItem.variantId,
      },
      type,
    );

    setVariantDecision(null);
  };

  const handleChooseAnotherVariant = () => {
    if (!variantDecision) {
      return;
    }

    setSelectedProduct(variantDecision.product); // opens existing product modal
    setVariantDecision(null);
  };

  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (
      typeof lat1 !== 'number' ||
      typeof lon1 !== 'number' ||
      typeof lat2 !== 'number' ||
      typeof lon2 !== 'number'
    ) {
      return null;
    }

    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return +(R * c).toFixed(2);
  };

  const getTravelTimeByCar = (distanceKm) => {
    if (typeof distanceKm !== 'number' || distanceKm <= 0) {
      return null;
    }
    const timeHours = distanceKm / DRIVE_SPEED_KMH;
    return Math.round(timeHours * 60);
  };

  const productCount = Object.values(
    searchResults.productsByMerchant || {},
  ).reduce((acc, { products }) => acc + (products?.length || 0), 0);

  const isPointInRadius = (point, center, radiusMeters) => {
    const toRad = (x) => (x * Math.PI) / 180;

    const R = 6371000; // Earth radius in meters
    const dLat = toRad(center.lat - point.lat);
    const dLng = toRad(center.lng - point.lng);
    const lat1 = toRad(point.lat);
    const lat2 = toRad(center.lat);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance <= radiusMeters;
  };

  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    const { lat: x, lng: y } = point;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat,
        yi = polygon[i].lng;
      const xj = polygon[j].lat,
        yj = polygon[j].lng;

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  };

  const cleanPolygon = (polygon) => {
    if (!Array.isArray(polygon)) {
      return [];
    }
    if (polygon.length <= 1) {
      return polygon;
    }
    // Remove duplicate last point if identical to the first
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) {
      return polygon.slice(0, -1);
    }
    return polygon;
  };

  const merchantIsServiceable = (merchant) => {
    if (
      !merchant?.zones ||
      !merchant?.activeZoneType ||
      !Array.isArray(merchant.zones) ||
      merchant.zones.length === 0
    ) {
      return false;
    }

    const userLocations = user?.locations || [];
    if (userLocations.length === 0) {
      return false;
    }

    const activeZoneType = merchant?.activeZoneType;

    for (const loc of userLocations) {
      // iterate over all user locations
      const point = { lat: loc.latitude, lng: loc.longitude };
      for (const zone of merchant.zones) {
        if (activeZoneType === 'radius') {
          if (zone.type === 'radius' && zone.center && zone.radius) {
            if (isPointInRadius(point, zone.center, zone.radius)) {
              return true;
            }
          }
        } else if (activeZoneType === 'geofence') {
          if (zone.type === 'geofence' && Array.isArray(zone.polygon)) {
            const cleaned = cleanPolygon(zone.polygon);
            if (isPointInPolygon(point, cleaned)) {
              return true; // AT LEAST one location inside AT LEAST one polygon
            }
          }
        }
      }
    }

    return false;
  };

  const filteredMerchants = searchResults?.merchants.filter((m) =>
    merchantIsServiceable(m),
  );

  return (
    <div className="mb-20 lg:mb-0">
      <div className="min-h-dvh flex flex-col">
        <Header />
        <MobileNavigation />

        <main className="flex-1 px-4 sm:px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-40 py-10">
          {searchTerm.trim() === '' ? (
            <div className="px-3 md:px-7 py-6 w-full text-[#b5b5b5]">
              <BrowseCategories />
            </div>
          ) : (
            <section className="pb-20 lg:pb-32">
              {/* Toggle buttons */}
              <div className="my-4 flex gap-2">
                <button
                  className={`px-4 py-2 rounded ${viewBy === 'products' ? 'bg-black text-white' : 'bg-gray-200'}`}
                  onClick={() => setViewBy('products')}
                >
                  {t('Products')} ({productCount})
                </button>
                <button
                  className={`px-4 py-2 rounded ${viewBy === 'merchants' ? 'bg-black text-white' : 'bg-gray-200'}`}
                  onClick={() => setViewBy('merchants')}
                >
                  {t('Brands')} ({filteredMerchants?.length || 0})
                </button>
              </div>

              {/* Loading spinner */}
              {loading && (
                <div className="text-center py-10">
                  <img
                    src={SpinnerIcon}
                    alt="Loading..."
                    className="h-10 w-10 animate-spin mx-auto"
                  />
                </div>
              )}

              {/* Results */}
              {!loading && (
                <>
                  {viewBy === 'merchants' &&
                    filteredMerchants?.length === 0 && (
                    <div className="text-center py-6">
                      {t('No merchants found')}
                    </div>
                  )}
                  {viewBy === 'products' &&
                    Object.keys(filteredProductsByMerchant).length === 0 && (
                    <div className="text-center py-6">
                      {t('No products found')}
                    </div>
                  )}

                  {viewBy === 'merchants' && filteredMerchants?.length > 0 && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredMerchants?.map((m) => {
                        const merchantRatingPercent = m.rating && typeof m.rating === 'object' && m.rating.count > 0
                          ? ratingToPercent(m.rating.averageRating)
                          : null;
                        return (
                          <div
                            key={m._id}
                            onClick={() => navigate(`/merchant-details/${m._id}`)}
                            className="nearby-merchants-card rounded-lg overflow-hidden cursor-pointer"
                          >
                            {/* Banner */}
                            <div
                              className="h-[140px] sm:h-[200px] w-full relative bg-cover bg-center"
                              style={{
                                backgroundImage: `url(${m.coverImage || FallbackBanner})`,
                              }}
                            >
                              {/* Rating */}
                              {merchantRatingPercent && (
                                <div className="text-xs leading-4 flex items-center justify-center gap-1 py-[6px] px-3 bg-black text-white rounded-[100px] absolute right-2 bottom-2">
                                  {merchantRatingPercent}
                                </div>
                              )}
                            </div>

                            {/* Details */}
                            <div className="bg-white py-[14px] px-3 md:px-4 flex items-center gap-3 flex-1">
                              {/* Category image */}
                              <div className="w-[62px] h-[62px] rounded-lg overflow-hidden">
                                <img
                                  src={m.logoImage || FallbackCategory}
                                  alt={m.storeName || 'merchant'}
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              <div className="flex flex-col gap-1 flex-1">
                                <h3 className="leading-[1.2] capitalize truncate font-semibold mb-[1px] text-[15.2px] text-balance line-clamp-1 ">
                                  {m.storeName || 'Unnamed Store'}
                                </h3>

                                <ul className="text-xs flex items-center font-medium text-[#6F6F6F]">
                                  {user?.locations?.[0] &&
                                  m?.address?.latitude != null &&
                                  m?.address?.longitude != null &&
                                  (() => {
                                    const distance = getDistanceKm(
                                      user.locations[0].latitude,
                                      user.locations[0].longitude,
                                      m.address.latitude,
                                      m.address.longitude,
                                    );
                                    const travelTime =
                                      getTravelTimeByCar(distance);

                                    return (
                                      <ul className="text-xs flex items-center font-medium text-[#6F6F6F]">
                                        {travelTime !== null && (
                                          <li>
                                            {travelTime} {t('mins')}
                                          </li>
                                        )}
                                        {distance !== null && (
                                          <li className="flex items-center">
                                            <div className="w-1 h-1 bg-[#6F6F6F] rounded-full mx-2"></div>
                                            {distance} {t('kilometers')}
                                          </li>
                                        )}
                                      </ul>
                                    );
                                  })()}
                                </ul>

                                <p className="font-medium text-xs leading-4 truncate text-[#8F8F8F]">
                                  {Array.isArray(m.categories?.enNames) &&
                                m.categories.enNames.length > 0
                                    ? m.categories.enNames.join(', ')
                                    : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {viewBy === 'products' &&
                    Object.values(filteredProductsByMerchant).map(
                      ({ merchantInfo, products }) => (
                        <div
                          key={merchantInfo._id}
                          className="border rounded mb-6 p-4 shadow bg-[#f6f6f6]"
                        >
                          <h3 className="font-semibold text-lg mb-2">
                            {merchantInfo.storeName}
                          </h3>

                          {/* Address + Rating */}
                          <div className="flex items-center text-sm text-[#969696] mb-4 space-x-2">
                            <span className="flex items-center">
                              {ratingToPercent(merchantInfo?.rating?.averageRating) ||
                                t('Unrated')}
                            </span>
                            <span className="truncate">
                              {user?.locations?.[0] &&
                                merchantInfo?.address?.latitude != null &&
                                merchantInfo?.address?.longitude != null &&
                                (() => {
                                  const distance = getDistanceKm(
                                    user.locations[0].latitude,
                                    user.locations[0].longitude,
                                    merchantInfo.address.latitude,
                                    merchantInfo.address.longitude,
                                  );
                                  const travelTime =
                                    getTravelTimeByCar(distance);

                                  return (
                                    <div className="flex items-center space-x-2 text-sm">
                                      {/* Travel Time */}
                                      {travelTime !== null && (
                                        <span className="flex items-center gap-1">
                                          <span className="inline-block w-1 h-1 rounded-full bg-[#969696]"></span>
                                          <span>
                                            {travelTime} {t('mins')}
                                          </span>
                                        </span>
                                      )}

                                      {/* Distance */}
                                      {distance !== null && (
                                        <span className="flex items-center gap-1">
                                          <span className="inline-block w-1 h-1 rounded-full bg-[#969696]"></span>
                                          <span>
                                            {distance} {t('kilometers')}
                                          </span>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                            </span>
                          </div>

                          <div className="relative">
                            {/* Left scroll button */}
                            <button
                              className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-200 p-2 rounded-full opacity-70 hover:opacity-100 z-10 hidden md:flex items-center justify-center"
                              onClick={() =>
                                scrollRow(merchantInfo._id, 'left')
                              }
                            >
                              <AiOutlineLeft className="w-6 h-6" />
                            </button>

                            {/* Product row */}
                            <div
                              id={`products-row-${merchantInfo._id}`}
                              className="flex overflow-x-auto gap-4 py-2 relative"
                              style={{
                                scrollbarWidth: 'none', // Firefox
                                msOverflowStyle: 'none', // IE & Edge
                              }}
                            >
                              {/* Hide scrollbar for Webkit browsers */}
                              <style>
                                {`
                                  #products-row-${merchantInfo._id}::-webkit-scrollbar {
                                    display: none;
                                  }
                                `}
                              </style>

                              {/* Products */}
                              {products.slice(0, 5).map((product) => (
                                <div
                                  key={product._id}
                                  className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[350px] min-h-[175px] border p-4 rounded cursor-pointer flex justify-between relative bg-white rounded-xl
                                  transition-all duration-300 ease-out
                                  hover:border-[#dddddd] hover:shadow-md"
                                  onClick={() => setSelectedProduct(product)}
                                >
                                  {/* Left */}
                                  <div className="flex flex-col justify-between pr-4 w-2/3">
                                    <div>
                                      <h3 className="font-semibold text-lg line-clamp-1">
                                        {i18n.language.startsWith('fr')
                                          ? product.frName
                                          : product.enName}
                                      </h3>
                                      <p
                                        className="text-sm text-gray-600 line-clamp-3"
                                        dangerouslySetInnerHTML={{
                                          __html: DOMPurify.sanitize(
                                            (
                                              (i18n.language.startsWith('fr')
                                                ? product.frDescription
                                                : product.enDescription) || ''
                                            ).replace(/\\n/g, '<br />'),
                                          ),
                                        }}
                                      />
                                    </div>

                                    <p className="mt-2 font-bold text-base leading-tight">
                                      {product?.price !== null ? (
                                        <span>
                                          {product?.price.toFixed(2)}$
                                        </span>
                                      ) : (
                                        <span className="text-xs text-[#999] leading-tight">
                                          {t('noPriceMessage')}
                                        </span>
                                      )}
                                    </p>
                                  </div>

                                  {/* Right */}
                                  <div className="w-[100px] h-[100px] sm:w-[120px] sm:h-[120px] md:w-[150px] md:h-[150px] rounded-lg border border-[#E1E1E1] flex items-center justify-center flex-shrink-0 relative">
                                    <div className="w-full h-full rounded-lg overflow-hidden">
                                      <img
                                        src={product.images?.[0] || BannerImage}
                                        alt={product.enName}
                                        className={`w-full h-full object-contain transition-all duration-300
                                          ${product.availability !== 'in-stock' ? 'filter grayscale brightness-90' : ''}`}
                                      />
                                      {product.availability !== 'in-stock' && (
                                        <div className="absolute top-0 left-0 w-full bg-black bg-opacity-50 py-1 px-2 flex items-center justify-center rounded-t-lg">
                                          <span className="text-white text-xs sm:text-sm font-semibold text-center">
                                            {t('Out of stock')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {product.availability === 'in-stock' && product?.price !== null && product?.price !== undefined && (
                                      getMaxVariantFromCart(product._id).quantity > 0 ? (
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] flex items-center bg-black text-white border border-black rounded px-2 py-1 text-sm font-medium">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleVariantQuantityIntent(
                                                product,
                                                'decrease',
                                              );
                                            }}
                                            className="px-2 text-lg"
                                          >
                                            −
                                          </button>

                                          <span className="px-3">
                                            {getMaxVariantFromCart(product._id).quantity}
                                          </span>

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleVariantQuantityIntent(
                                                product,
                                                'increase',
                                              );
                                            }}
                                            className="px-2 text-lg"
                                          >
                                            +
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            if (product.variants?.length > 0) {
                                              return;
                                            }
                                            e.stopPropagation();
                                            addToCart(product);
                                          }}
                                          className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] bg-white text-black text-sm font-medium px-5 py-1.5 rounded border border-[#E1E1E1] hover:bg-[#33b0d4] transition duration-300 whitespace-nowrap capitalize"
                                        >
                                          {t('add')} +
                                        </button>
                                      ))}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Right scroll button */}
                            <button
                              className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-200 p-2 rounded-full opacity-70 hover:opacity-100 z-10 hidden md:flex items-center justify-center"
                              onClick={() =>
                                scrollRow(merchantInfo._id, 'right')
                              }
                            >
                              <AiOutlineRight className="w-6 h-6" />
                            </button>

                            {/* See all button */}
                            {products.length > 5 && (
                              <div className="mt-2 flex justify-center">
                                <button
                                  className="text-blue-600 font-semibold"
                                  onClick={() =>
                                    navigate(
                                      `/${merchantInfo._id}/search?query=${encodeURIComponent(searchTerm)}`,
                                    )
                                  }
                                >
                                  {t('See all')} <span>&gt;</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                </>
              )}
            </section>
          )}

          <Toast message={toastMessage} type={toastType} />

        </main>

        {variantDecision && (
          <div
            className="fixed inset-0 bg-black/40 flex items-end justify-center z-50"
            onClick={() => setVariantDecision(null)}
          >
            <div
              className="
                      bg-white w-full max-w-lg
                      p-6
                      shadow-lg
                      animate-slide-up2
                    "
              onClick={(e) => e.stopPropagation()}
            >
              <div className="-mx-6 -mt-6 mb-4 bg-[#ccc] px-6 py-4">
                <h3 className="text-lg font-semibold leading-tight">
                  {i18n.language.startsWith('fr')
                    ? variantDecision.product?.frName
                    : variantDecision.product?.enName}
                </h3>
              </div>

              <p className="text-sm mb-6">
                {t('previousSelections')}:{' '}
                {i18n.language.startsWith('fr')
                  ? variantDecision.variantItem?.frVariantName
                  : variantDecision.variantItem?.enVariantName}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleChooseAnotherVariant}
                  className="flex-1 border border-black bg-white py-3 font-medium hover:bg-gray-100"
                >
                  {t('chooseVariant')}
                </button>

                <button
                  onClick={handleModifySameVariant}
                  className="flex-1 bg-black text-white border border-gray-300 py-3 font-medium"
                >
                  {t('repeat')}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedProduct && (
          <SingleProductDetails
            product={selectedProduct}
            cartQuantities={getVariantQuantitiesFromCart(selectedProduct._id)}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default GlobalSearch;
